import {useCallback, useMemo, useState} from 'react';
import {FileData} from 'types';
import useLocalStorageState from './useLocalStorageState';
import {LOCAL_STORE_CONFIG_NAMES} from 'const';
import {sortBy} from 'lodash';

export const SORT_OPTIONS = [
   {key: 'name', label: 'Name'},
   {key: 'modified', label: 'Last Modified'},
   {key: 'createdDate', label: 'Created'},
];

export function useSortingMenu(files?: FileData[]) {
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // Anchor for sort menu
   const [sortOption, setSortOption] = useLocalStorageState(
      LOCAL_STORE_CONFIG_NAMES.sortOption,
      SORT_OPTIONS[0].key
   ); // Current sort option
   const [sortDirection, setSortDirection] = useLocalStorageState<'asc' | 'desc'>(
      LOCAL_STORE_CONFIG_NAMES.sortDirection,
      'asc'
   );

   // Handle sorting menu open/close
   const handleSortMenuOpen = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
         setAnchorEl(event.currentTarget);
      },
      [setAnchorEl]
   );

   const handleSortMenuClose = useCallback(
      (option?: string) => {
         if (option) setSortOption(option);
         setAnchorEl(null);
      },
      [setAnchorEl, setSortOption]
   );

   // Toggle sorting direction
   const toggleSortDirection = useCallback(() => {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
   }, [setSortDirection]);

   const sortedFiles = useMemo(
      () =>
         files
            ? [...files].sort((a, b) => {
                 const aIsFolder = a.type === 'folder';
                 const bIsFolder = b.type === 'folder';

                 // 1. folders first
                 if (aIsFolder !== bIsFolder) {
                    return aIsFolder ? -1 : 1;
                 }

                 let result = 0;

                 // 2. sort inside folders / files
                 if (sortOption === 'name') {
                    result = a.name.localeCompare(b.name, undefined, {
                       sensitivity: 'base', // case-insensitive
                    });
                 }

                 if (sortOption === 'modified') {
                    result =
                       new Date(a.modifiedAt ?? 0).getTime() -
                       new Date(b.modifiedAt ?? 0).getTime();
                 }

                 if (sortOption === 'createdDate') {
                    result =
                       new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime();
                 }

                 return sortDirection === 'asc' ? result : -result;
              })
            : [],
      [files, sortOption, sortDirection]
   );

   return {
      sortedFiles,
      handleSortMenuClose,
      handleSortMenuOpen,
      anchorEl,
      sortOption,
      toggleSortDirection,
      sortDirection,
   };
}

export type useSortingType = ReturnType<typeof useSortingMenu>;
